import React, { useEffect, useState } from 'react'
import { InputField, Modal, Select } from '@enamelai/design-system'
import { CANCEL_MEMBERSHIP_REASONS } from '@constants/Membership/membershipPage.constants'
import { useNotificationToast } from '@context/NotificationToastContext'
import type { MembershipPageMember } from '@dataTypes/membership.type'
import { updateMembershipMemberStatus } from '@store/actions/membership.action'
import { useAppDispatch } from '@store/hooks'
import {
  createFailedToast,
  createMembershipOnHoldToast,
} from '@utils/notificationToast.util'
import './TemporaryHoldModal.css'

type TemporaryHoldModalProps = {
  open: boolean
  member?: MembershipPageMember | null
  onClose: () => void
  onHeld?: (member: MembershipPageMember) => void
}

const TemporaryHoldModal: React.FC<TemporaryHoldModalProps> = ({
  open,
  member,
  onClose,
  onHeld,
}) => {
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const [reason, setReason] = useState('')
  const [otherReason, setOtherReason] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) return

    setReason('')
    setOtherReason('')
    setSaving(false)
  }, [open])

  const memberName = member?.name ?? 'Patient'

  const handleClose = (): void => {
    onClose()
  }

  const handleProceed = (): void => {
    if (!member || saving) return

    const statusReason = [reason.trim(), otherReason.trim()]
      .filter(Boolean)
      .join(': ')

    setSaving(true)
    void dispatch(
      updateMembershipMemberStatus({
        id: member.id,
        status: 'on_hold',
        ...(statusReason ? { reason: statusReason } : {}),
      }),
    ).then((result) => {
      setSaving(false)

      if (updateMembershipMemberStatus.rejected.match(result)) {
        showToast(
          createFailedToast(
            'Unable to hold membership',
            result.payload || 'Failed to place membership on hold.',
          ),
        )
        return
      }

      showToast(createMembershipOnHoldToast(memberName))
      onHeld?.(result.payload)
      handleClose()
    })
  }

  const handleReasonChange = (value: string | string[]): void => {
    setReason(typeof value === 'string' ? value : (value[0] ?? ''))
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="md"
      title={`Temporarily hold membership: ${memberName}`}
      className="temporary-hold-modal"
      bodyClassName="temporary-hold-modal__body"
      primaryLabel="Proceed"
      primaryDisabled={saving}
      secondaryLabel="Cancel"
      onPrimaryClick={handleProceed}
      onSecondaryClick={handleClose}
    >
      <Select
        className="temporary-hold-modal__select"
        label="Reason for canceling"
        placeholder="Select here"
        options={CANCEL_MEMBERSHIP_REASONS}
        value={reason}
        onChange={handleReasonChange}
      />
      <InputField
        className="temporary-hold-modal__notes"
        label="Others reasons"
        variant="message"
        placeholder="Write here..."
        rows={3}
        maxLength={100}
        showCounter
        value={otherReason}
        onChange={(event) => setOtherReason(event.target.value)}
      />
    </Modal>
  )
}

export default TemporaryHoldModal
